import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpClient, HttpContext } from '@angular/common/http';

import { environment } from 'src/environments/environment';
import { Project, Part } from '~shared/types';
import { ProjectStatus } from '~shared/enums';
import { SKIP_AUTH } from '~core/interceptors/header.interceptor';

@Injectable({ providedIn: 'root' })
export class ProjectService {
	private projectUrl: string = environment.projectServiceUrl + 'project';
	private partsUrl: string = environment.projectServiceUrl + 'part';

	constructor(
		private http: HttpClient,
	) {}

	/**
	 * Fetches projects using query parameters.
	 * Returns enriched projects with accountName populated.
	 * Only returns the most recent version of each project.
	 */
	getProjects(accountId?: string): Observable<Project[]> {
		return this.http
			.get<Project[]>(this.projectUrl, {
				params: { ...(accountId && { account_id: accountId }) }
			});
	}

	/**
	 * Fetches a single enriched project by ID using query parameters.
	 * Returns the most recent version with accountName populated.
	 */
	getProject(projectId: string, context?: HttpContext): Observable<Project> {
		return this.http
			.get<Project[]>(this.projectUrl, {
				params: { project_id: projectId },
				context,
			})
			.pipe(map((projects) => projects[0]));
	}

	/**
	 * Fetches the history of a project (different statuses with timestamps).
	 * Does NOT include accountName.
	 */
	getProjectHistory(id: string): Observable<[Project]> {
		return this.http
			.get<[Project]>(this.projectUrl + `/${id}`)
	}

	updateProject(projectId: string, project: Partial<Project>): Observable<Project> {
		return this.http
			.put<Project>(this.projectUrl + `/${projectId}`, project);
	}

	createProject(project: Partial<Project>): Observable<Project> {
		return this.http
			.post<Project>(this.projectUrl, project);
	}

	deleteProject(id): Observable<void> {
		return this.http
			.delete<void>(this.projectUrl + `/${id}`);
	}

	duplicateProject(id): Observable<Project> {
		return this.http
			.post<Project>(this.projectUrl + `/${id}`, {});
	}

	addWallColour(projectId: string, colour: string): Observable<Project> {
		return this.http
			.patch<Project>(`${this.projectUrl}/${projectId}/wall-colours`, { colour });
	}

	updateProjectStatus(projectId: string, status: ProjectStatus): Observable<Project> {
		return this.http
			.patch<Project>(`${this.projectUrl}/${projectId}/status`, {
				status
			});
	}

	public editProject(newProject: Project, id): Observable<Project> {
		return this.http.get<Project>(this.partsUrl);
	}

	public getPartsByProjectId(projectId: string): Observable<Part[]> {
		return this.http
			.get<Part[]>(this.partsUrl, {
				params: {
					project_id: projectId
				}
			});
	}

	public getParts$(projectId: string): Observable<Part[]> {
		return this.http
			.get<Part[]>(this.partsUrl, {
				params: { project_id: projectId }
			});
	}

	public getPart$(partId: string): Observable<Part> {
		return this.http
			.get<Part>(`${this.partsUrl}/${partId}`, {
				context: new HttpContext().set(SKIP_AUTH, true)
			});
	}


	public getPartHistory(partId: string): Observable<Part[]> {
		return this.http
			.get<Part[]>(`${this.partsUrl}/${partId}/history`);
	}

	public addCustomisationToPart(partId: string, customisationId: string): Observable<Part> {
		return this.http
			.post<Part>(`${this.partsUrl}/${partId}/customisation/${customisationId}`, {});
	}

	public deleteCustomisationFromPart(partId: string, customisationId: string): Observable<Part> {
		return this.http
			.delete<Part>(`${this.partsUrl}/${partId}/customisation/${customisationId}`, {});
	}

	public changePartStatus(part_id: string, status: string): Observable<Part[]> {
		const body = {
			status,
		};
		return this.http
			.patch<Part[]>(this.partsUrl + '/' + part_id, body);
	}

	public editPart(newPart: Part): Observable<Part> {
		return this.http
			.put<Part>(this.partsUrl + '/' + newPart.id, newPart);
	}

	public deletePart(part_id) {
		return this.http
			.delete(this.partsUrl + '/' + part_id);
	}

	public createPart(to_create): Observable<Part> {
		return this.http
			.post<Part>(this.partsUrl, to_create);
	}

	public updatePart(partId, part): Observable<Part> {
		return this.http
			.put<Part>(`${this.partsUrl}/${partId}`, part);
	}

	public duplicatePart(partId: string): Observable<Part> {
		return this.http
			.post<Part>(`${this.partsUrl}/${partId}`, {});
	}

	public updatePartStatus(partId: string, status): Observable<Part> {
		return this.http
			.patch<Part>(`${this.partsUrl}/${partId}/status`, { status });
	}

}
